import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {AgGridReact} from "ag-grid-react";
import {Box, Chip, CircularProgress, Menu, MenuItem} from "@mui/material";
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import {blue, grey, red} from "@mui/material/colors";

import {useTranslation, withTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {cloneDeep, cloneDeepWith, includes, isEmpty, isEqual, isNil, isNull, isUndefined} from "lodash";

import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import DataState from "@plannel/components/aggrid/DataState";
import NumericEditor from "@plannel/components/aggrid/NumericEditor";
import GridDatePicker from "@plannel/components/GridDatePicker";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import {
  AddButton,
  RemoveButton,
  ResultButton,
  RunButton,
  RunningButton,
  SaveButton
} from "@plannel/components/ActionIconButton";
import NumberUtil from "@plannel/utils/number-util";
import DateUtils from "@plannel/utils/date-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import abcXyzAnalysisScenariosService from "@plannel/services/ip/abc-analysis-scenarios-service";
import classificationService from "@plannel/services/ip/classification-service";

import "@plannel/scss/components/AbcXyzAnalysis.scss";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import {reloadClassificationFilter} from "@plannel/components/filter/ClassificationFilter";
import {addMonths} from "date-fns";
import { FS } from "@plannel/utils/font-util";

const CURRENT_DATE = new ZDate();
const MAX_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 0);
const MIN_DATE = new ZDate(MAX_DATE.getFullYear(), MAX_DATE.getMonth() + 1, 1);
const DIALOG_TITLE = { SAVE: "SAVE", DELETE: "DELETE", RUN: "RUN_ANALYSIS" };
const { ITEM, CATEGORY, LOCATION, CATEGORY_LOCATION } = CodeUtil.classifiDimLCode;
const { QUANTITY, REVENUE, AMOUNT, PROFIT, COGS } = CodeUtil.classifiBaseCode;
const { READY, RUNNING, UNPLANNED, PLANNED } = CodeUtil.versionStatusCode;
const itemType = CodeUtil.itemType;

const CLASSIFI_DIM_LIST = [ITEM, CATEGORY, LOCATION, CATEGORY_LOCATION];
const BASE_LIST = [QUANTITY, REVENUE, PROFIT, COGS];
const BASE_CV = "baseCv";
const FORECAST_GAP = "FORECAST_GAP";
const XYZ_CALC_TYPE = [BASE_CV, FORECAST_GAP];
const UOM_LIST = ["month"];
const TARGET_ITEM_TYPE = [itemType.FG.cd, itemType.RM.cd];
const ACTUAL_TYPE_SALES = "sales";
const ACTUAL_TYPE_SHIPMENT = "shipment";
const UNCERTAINTIES = "uncertainties";
const DEPRECATED_CV = ["cov", "cv"];

const CLASSIFICATION_GRID = "CLASSIFICATION_GRID";
const ABC_XYZ_GRID = "ABC_XYZ_GRID";
const ABC_XYZ_ANALYSIS_RESULT = "abcXyzAnalysisResult";
const CLASSIFICATION_RESULT = "classificationResult";

const A_CLASS_DEFAULT = 70;
const B_CLASS_DEFAULT = 20;
const X_CLASS_DEFAULT = 25;
const Y_CLASS_DEFAULT = 50;
const HEADER_CLICK_SOURCE = "uiSelectAllFiltered";
const API_CLICK_SOURCE = "apiSelectAll";
const NONE = "NONE";
const EXCLUDE_CLICK_EVENT_SOURCES = [HEADER_CLICK_SOURCE, API_CLICK_SOURCE];
const { snackBarMessageColor } = ColorUtil;
const AbcXyzAnalysisScenarios = ({ t, viewName, title }) => {
  const reduxDispatch = useDispatch();
  const reduxViewState = reduxUtil.getViewState(viewName);
  const { isPermitted } = useMenuAccess();

  const [isRunning, setIsRunning] = useState(false);
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const [classifications, setClassifications] = useState([]);

  const [boundaryNumbers, setBoundaryNumbers] = useState({ a: A_CLASS_DEFAULT, b: A_CLASS_DEFAULT + B_CLASS_DEFAULT, x: X_CLASS_DEFAULT, y: Y_CLASS_DEFAULT });
  const [classificationResultSummary, setClassificationResultSummary] = useState([]);
  const [chartData, setChartData] = useState([]);
  const categorySelectionSyncScheduled = useRef(false);

  const selectedClassification = useRef();

  const classificationGridRef = useRef();
  const abcXyzGridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const addListOpen = Boolean(anchorEl);
  const searchCode = useRef("");
  const defaultMainGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: CLASSIFICATION_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defualtResultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title: ABC_XYZ_ANALYSIS_RESULT, viewName, gridId: ABC_XYZ_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBaseList = (params) => {
    const data = params?.data;
    const plantLocAnalysis = data?.plantLocAnalysis;

    if (plantLocAnalysis === true) {
      return [QUANTITY, AMOUNT]; /* PLANT 분석인경우 REVENUE는 AMOUNT로 대체, PROFIT/COGS는 제외 */
    }

    return BASE_LIST;
  }

  const getXYZCalcTypeList = (params) => {
    const data = params?.data;
    const plantLocAnalysis = data?.plantLocAnalysis;

    if (plantLocAnalysis === true) {
      return [BASE_CV];
    }

    return XYZ_CALC_TYPE;
  }

  const getDefaultScenarioList = () => {
    const baseRow = {
      id: null,
      name: null,
      statusCd: UNPLANNED,
      periodUom: UOM_LIST[0],
      plantLocAnalysis: false,
      targetItemType: null,
      actualPeriodVal: null,
      actualPeriodEnd: MAX_DATE,
      demandPeriodStart: MIN_DATE,
      demandPeriodVal: null,
      classifiDimCd: null,
      classifiObj: {},
      base: null,
      xyzCalcType: BASE_CV, // baseCv: other. ,cv: target Selection
      aClass: 0,
      bClass: 0,
      cClass: 0,
      xClass: 0,
      yClass: 0,
      zClass: 0,
      newProduct: 0,
      noSales: 0,
      eosPlanned: 0,
      verNum: 0
    };

    const defaultBase = {
      id: null,
      name: null,
      statusCd: UNPLANNED,
      periodUom: UOM_LIST[0],
      plantLocAnalysis: false,
      targetItemType: null,
      actualPeriodVal: 6,
      actualPeriodEnd: MAX_DATE,
      demandPeriodStart: MIN_DATE,
      demandPeriodVal: 0,
      classifiDimCd: null,
      classifiObj: {},
      base: BASE_LIST[0],
      xyzCalcType: BASE_CV,
      aClass: A_CLASS_DEFAULT,
      bClass: B_CLASS_DEFAULT,
      cClass: 100 - A_CLASS_DEFAULT - B_CLASS_DEFAULT,
      xClass: X_CLASS_DEFAULT,
      yClass: Y_CLASS_DEFAULT,
      zClass: Y_CLASS_DEFAULT,
      newProduct: 0,
      noSales: 0,
      eosPlanned: 0,
      verNum: 0
    };

    defaultBase.name = `${defaultBase.aClass / 10}:${defaultBase.bClass / 10}:${defaultBase.cClass / 10} on Actual ${defaultBase.actualPeriodVal}M, Demand ${defaultBase.demandPeriodVal}M, ${defaultBase.base}`;

    const defaultScenarios = [{ label: "Empty", value: baseRow }];

    for (const classifiDimCd of CLASSIFI_DIM_LIST) {
      const defaultRow = cloneDeepWith(defaultBase);
      const domainValue = GridUtils.gridValueL10N(classifiDimCd);

      defaultRow.classifiDimCd = classifiDimCd;
      defaultRow.name += `, ${domainValue}`;

      defaultScenarios.push({ label: defaultRow.name, value: defaultRow });
    }

    return defaultScenarios;
  }

  const defaultScenarioList = useMemo(() => {
    return getDefaultScenarioList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 결과 화면이 권한 밖이면 셀을 링크로 만들지 않는다(아래 onCategoryCellClicked).
  const canOpenResults = isPermitted("ABC_XYZ_RESULTS");

  const showResultsPage = (classification, category) => {
    const filter = isNil(category) ? {category: []} : { category: [category] };
    const reduxKey = "ABC_XYZ_RESULTS";
    const viewState = reduxUtil.createObjectState(reduxKey, { filter, classification });

    reduxDispatch(reduxUtil.updateViewState(viewState));
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  }

  const onCategoryCellClicked = (params) => {
    let { value } = params;
    const { categoryCd, categoryName } = params?.data;

    if (value === NONE) {
      value = GridUtils.gridValueL10N(value);
    }

    if (!canOpenResults) return <div>{value}</div>;

    return (
      <div>
        <span
          tabIndex={0}
          style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
          onClick={() => showResultsPage(selectedClassification.current, { hrchyCd: categoryCd, name: categoryName, leafFlg: true })}
          role="button">
          {value}
        </span>
      </div>
    )
  }

  const valueFormatter = params => {
    return GridUtils.gridValueL10N(params.value);
  }

  // 그리드 셀도 위 매트릭스 타일과 같은 A안 색(hue 유지 + 흰색 18% 혼합) — 공용 헬퍼로 일원화.
  const classCellStyle = (params) => {
    const headerName = params?.colDef?.headerName;
    const bg = ColorUtil.abcXyzTileBg(headerName);
    if (bg) {
      return { backgroundColor: bg };
    }
    return {};
  }

  const categoryValueFormatter = (params) => {
    const value = params?.value;

    if (value === NONE) {
      return GridUtils.gridValueL10N(value);
    }

    return value;
  }

  const categoryColumnDefs = [
    { headerName: "categoryCd", field: "categoryCd", type: ["nonEditableColumn"], width: 120, hide: true },
    {
      headerName: "categoryName",
      field: "categoryName",
      type: ["nonEditableColumn"],
      cellRenderer: onCategoryCellClicked,
      valueFormatter: categoryValueFormatter,
      width: 200,
    },
    { headerName: "base", field: "baseCd", type: ["nonEditableColumn"], valueFormatter, width: 90 },
    { headerName: "AX", field: "axClass", cellStyle: classCellStyle, type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 75 },
    { headerName: "AY", field: "ayClass", cellStyle: classCellStyle, type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 75 },
    { headerName: "AZ", field: "azClass", cellStyle: classCellStyle, type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 75 },
    { headerName: "BX", field: "bxClass", cellStyle: classCellStyle, type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 75 },
    { headerName: "BY", field: "byClass", cellStyle: classCellStyle, type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 75 },
    { headerName: "BZ", field: "bzClass", cellStyle: classCellStyle, type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 75 },
    { headerName: "CX", field: "cxClass", cellStyle: classCellStyle, type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 75 },
    { headerName: "CY", field: "cyClass", cellStyle: classCellStyle, type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 75 },
    { headerName: "CZ", field: "czClass", cellStyle: classCellStyle, type: ["numberColumn", "nonEditableColumn", "rightAligned"], width: 75 }
  ];

  const checkPlanned = (statusCd) => {
    if (statusCd === READY) {
      return true;
    }
    return statusCd === PLANNED;
  }

  const retrieveClassificationResultSummary = (selectedClassificationParam) => {
    if (!isNil(selectedClassificationParam) && !isEmpty(selectedClassificationParam)) {
      const status = selectedClassificationParam.statusCd;
      if (selectedClassificationParam?.id && checkPlanned(status)) {
        const classificationId = selectedClassificationParam.id;

        abcXyzAnalysisScenariosService.get(classificationId)
          .then(response => {
            const results = response.data;

            /* Response가 현재 선택된 Row와 일치하는 경우에만 처리. 
            * Row를 클릭 후 API 응답이 오기 전에 다른 Row를 클릭하는 경우가 있어, 
            * 응답이 늦게 도착하는 경우 이전에 선택된 Row의 결과가 보여지는 이슈 방지 위해 추가
            */
           const currentSelectedClassificationId = selectedClassification.current?.id;
            if (currentSelectedClassificationId === classificationId) {
              setClassificationResultSummary(results);
            }
          })
          .catch(e => {
            debug(e);
          })
      }
    } else {
      setClassificationResultSummary([]);
    }
  };

  const postProcessClassifications = (results) => {
    const processedClassifications = [];

    results.forEach((data) => {
      const classifiObj = data.classifiObj;

      if (classifiObj) {
        Object.entries(classifiObj).forEach((el) => {
          const key = el[0];
          const value = el[1];

          if (key === "plantLocAnalysis") {
            data[key] = value === true || String(value).toLowerCase() === "true";
          } else if (!isNil(value) && DEPRECATED_CV.includes(value)) {
            data[key] = BASE_CV;
          } else {
            data[key] = value;
          }
        });
      }

      processedClassifications.push(data);
    });

    setClassifications(processedClassifications);
  }

  const getRequestParams = () => {
    const params = {};

    if (searchCode.current) {
      params.name = searchCode.current;
    }

    params.orderByColumn = "updatedTs";
    params.sortType = "desc";

    return params;
  };

  const retrieveClassifications = () => {
    classificationService.getAll(getRequestParams())
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          postProcessClassifications([]);
          setClassificationResultSummary([]);
          setChartData([]);
        } else {
          postProcessClassifications(results);
        }

        DataState.initialize(classificationGridRef.current?.api);
      })
      .catch(e => {
        debug(e);
      });
  };

  const setSelectedClassification = (classification) => {
    selectedClassification.current = classification;

    retrieveClassificationResultSummary(classification);
  }

  const runAnalysis = () => {
    setClassificationResultSummary([]);
    setChartData([]);
    setIsRunning(true);

    abcXyzAnalysisScenariosService.run(selectedClassification.current)
      .then(response => {
        const { status } = response;
        if (status === 204) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_ABC_XYZNoResult") });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        }
      })
      .catch(e => {
        const { status } = e.response;
        if (status === 409) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.optimisticLock, content: t("MSG_OptimisticLockAboutTarget", { target: selectedClassification.current.name }) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_ABC_XYZExcutedFailed") });
        }

        debug(e);
      })
      .finally(() => {
        setIsRunning(false);
        retrieveClassifications();
      })
  }

  const onCategoryGridRowClickedHandler = () => {
    const api = abcXyzGridRef.current?.api;

    if (!isEmpty(api)) {
      const selectedCategories = [];
      const selectedRows = api.getSelectedRows();
      setChartData(selectedRows);

      for (const row of selectedRows) {
        if (row?.categoryCd && !selectedCategories.includes(row.categoryCd)) {
          selectedCategories.push(row.categoryCd);
        }
      }

      const reduxState = reduxUtil.createObjectState(viewName, { selectedCategories });
      reduxDispatch(reduxUtil.updateViewState(reduxState));
    }
  }

  const syncCategorySelectionOnce = () => {
    if (categorySelectionSyncScheduled.current) {
      return;
    }

    categorySelectionSyncScheduled.current = true;

    // RowSelected fires once per row during select-all. Coalesce into one sync.
    setTimeout(() => {
      onCategoryGridRowClickedHandler();
      categorySelectionSyncScheduled.current = false;
    }, 0);
  }

  const onRowSelected = (params, identifier) => {
    const { node } = params;
    if (identifier === "SCENARIO") {
      if (node.isSelected()) {
        setClassificationResultSummary([]);
        setChartData([]);

        const selectedRows = params.api.getSelectedRows();
        const selectedRow = selectedRows.length > 0 ? selectedRows[0] : {};
        setSelectedClassification(selectedRow);

        if (selectedRow?.id) {
          const selectRowInfo = classifications.find((el) => el.id === selectedRow.id);

          if (selectRowInfo?.classifiObj) {
            const classifiObj = selectRowInfo.classifiObj;
            const selectedBoundaryNumbers = {
              a: classifiObj?.aClass ? classifiObj.aClass : "N/A",
              b: classifiObj?.bClass ? classifiObj.bClass : "N/A",
              x: classifiObj?.xClass ? classifiObj.xClass : "N/A",
              y: classifiObj?.yClass ? classifiObj.yClass : "N/A",
            };

            setBoundaryNumbers(selectedBoundaryNumbers);
          }
        }

        const reduxState = reduxUtil.createObjectState(viewName, { classification: selectedRow });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      }

    } else if (identifier === "CATEGORY") {
      const sourceType = params?.source;
      const event = params?.event;

      if (!includes(EXCLUDE_CLICK_EVENT_SOURCES, sourceType) && !isNull(event)) {
        /* Row를 하나씩 클릭 하는 경우 */
        syncCategorySelectionOnce();
      } else if (includes(EXCLUDE_CLICK_EVENT_SOURCES, sourceType) || isNull(event)) {
        /* Header의 Click을 클릭하는 경우 (API call, header check-box, shift key) */
        syncCategorySelectionOnce();
      }
    }
  }

  const onCellValueChanged = (params) => {
    const { column, node, data } = params;
    const classColumns = ["aClass", "bClass"];
    const classTargetColumn = "cClass";
    const yColumn = "yClass";
    const targetZColumn = "zClass";
    const editedColId = column.getColId();

    if (classColumns.includes(editedColId)) {
      let sum = 0;

      for (const colName of classColumns) {
        if (!data[colName] || data[colName] !== "0") {
          sum += Number(data[colName]);
        } else {
          break;
        }
      }

      node.setDataValue(classTargetColumn, 100 - sum);
    }

    if (yColumn === (editedColId)) {
      node.setDataValue(targetZColumn, data[yColumn]);
    }

    if (editedColId === "xyzCalcType") {
      const xyzCalcTypeValue = data[editedColId];
      if (xyzCalcTypeValue === FORECAST_GAP) {
        node.setDataValue("demandPeriodVal", 0);
      }
    }

    const demandPeriodStart = data?.demandPeriodStart;
    const actualPeriodEnd = data?.actualPeriodEnd;
    if (!isNil(demandPeriodStart) && !isNil(actualPeriodEnd) && demandPeriodStart <= actualPeriodEnd) {
      const actualEndDate = new ZDate(data?.actualPeriodEnd);
      const newStartDate = addMonths(actualEndDate, 1);

      node.setDataValue("demandPeriodStart", DateUtils.formatDate(newStartDate, "yyyy-MM-dd"));
    }

    DefaultGridSetting({}).onCellValueChanged(params);
  }

  const targetItemTypeValueFormatter = (param) => {
    const value = param?.value;
    const node = param?.node;
    const data = node?.data;

    if (!isNil(node) &&data?.plantLocAnalysis !== true) {
      return "";
    }

    if (isNil(value)) {
      return null;
    }

    const name = itemType?.[value]?.name;
    if (isNil(name)) {
      return GridUtils.gridValueL10N(value);
    }

    return GridUtils.gridValueL10N(name);
  }

  const cellPercentValueFormatter = (param) => {
    const postFix = "%";
    return param.value + postFix;
  }

  // 계획 버전 상태 → 채움-틴트 칩 (버전 드롭다운/분류 모달 배지와 동일 규격)
  const statusCellRenderer = (params) => {
    const statusCd = params.value;
    if (isNull(statusCd) || isUndefined(statusCd) || statusCd === "") return statusCd ?? "";
    const label = CodeUtil.versionStatusLabel(statusCd);
    return <Chip size="small" label={label} sx={{ height: 22, fontSize: FS.BODY, ...ColorUtil.tintChipSx(ColorUtil.versionStatusColor(statusCd)) }} />;
  }

  const percentValueCellRenderer = (params) => {
    let arrow = "";

    if (params.column.getColId() === "xClass" || params.column.getColId() === "yClass") {
      arrow = <ArrowDownward color={blue[600]} fontSize="1.5rem" />;
    } else if (params.column.getColId() === "zClass") {
      arrow = <ArrowUpward color={red[600]} fontSize="1.5rem" />;
    }

    if (!isNull(params.value) && !isUndefined(params.value)) {
      return <div>{cellPercentValueFormatter(params)}{arrow}</div>;
    }

    return params.value;
  }

  const demandPlanEditableHandler = (params) => {
    const xyzCalcType = params?.data?.xyzCalcType;
    const plantLocAnalysis = params?.data?.plantLocAnalysis;

    if (plantLocAnalysis === true) { // 생산시설인 경우 수요계획 입력 불가
      return false;
    }

    return xyzCalcType !== FORECAST_GAP;
  }

  const demandPlanCellStyle = (params) => {
    const style = {};

    const xyzCalcType = params?.data?.xyzCalcType;
    const plantLocAnalysis = params?.data?.plantLocAnalysis;

    if (isEqual(xyzCalcType, FORECAST_GAP) || plantLocAnalysis === true) {
      style.backgroundColor = grey[300];
    }

    return style;
  }

  const targetItemTypeCellStyle = (params) => {
    const style = {};

    const plantLocAnalysis = params?.data?.plantLocAnalysis;

    if (plantLocAnalysis !== true) {
      style.backgroundColor = grey[300];
    }

    return style;
  }

  const dateValueFormatter = (params) => {
    const value = params?.value;

    if (isNil(value)) {
      return "";
    }

    return DateUtils.formatDate(value, "yyyy-MM");
  }

  const scenarioGridColumnDefs = [
    { headerName: "id", field: "id", type: ["nonEditableColumn", "defaultValueFormatter"], width: 0, hide: true, suppressColumnsToolPanel: true },
    { headerName: "classificationName", field: "name", width: 280 },
    { headerName: "status", type: ["nonEditableColumn"], field: "statusCd", width: 100, cellRenderer: statusCellRenderer },
    {
      headerName: "periodUom", field: "periodUom",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      singleClickEdit: true,
      valueFormatter,
      cellEditorParams: {
        values: UOM_LIST
      },
      width: 95
    },
    { 
      headerName: "plant", field: "plantLocAnalysis",
      type: ["booleanColumn"],
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      valueSetter: (params) => {
        const rawValue = params.newValue;
        const newValue = rawValue === true || String(rawValue).toLowerCase() === "true";

        params.data.plantLocAnalysis = newValue;
        if (newValue === false) {
          params.data.actualType = ACTUAL_TYPE_SALES;
          params.data.targetItemType = null;
        } else if (newValue === true) {
          params.data.actualType = ACTUAL_TYPE_SHIPMENT;
          params.data.targetItemType = itemType.FG.cd;
          params.data.xyzCalcType = BASE_CV;
          params.data.demandPeriodStart = null;
          params.data.demandPeriodVal = null;

          const base = params.data.base;
          if (base === PROFIT || base === COGS) {
            params.data.base = QUANTITY;
          }
        }

        return true;
      },
      width: 120
    },
    {
      headerName: "targetItemType", field: "targetItemType",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: targetItemTypeValueFormatter,
      cellEditorParams: {
        values: TARGET_ITEM_TYPE
      },
      width: 140,
      editable: (params) => params?.data?.plantLocAnalysis === true,
      cellStyle: targetItemTypeCellStyle
    },
    {
      headerName: "actual", groupId: "actualSalesGrp",
      children: [
        {
          headerName: "actualType",
          field: "actualType",
          type: ["nonEditableColumn"],
          valueGetter: (params) => {
            const value = params?.data.actualType;

            if (isNil(value)) {
              return ACTUAL_TYPE_SALES;
            }

            return value;
          },
          valueFormatter: (params) => {
            const value = params?.value;

            return GridUtils.gridValueL10N(value);
          },
          width: 110
        },
        {
          headerName: "numOfMonths",
          field: "actualPeriodVal",
          cellEditor: NumericEditor,
          cellEditorParams: {
            setSnackInfo,
            min: 0,
            max: 120,
          },
          type: ["numberColumn", "rightAligned"],
          valueFormatter: (params) => {
            const value = params?.value;

            if (isNil(value) || value === 0) {
              return "";
            }

            return value;
          },
          width: 110
        },
        {
          headerName: "periodEnd",
          cellDataType: 'text',
          field: "actualPeriodEnd",
          type: ["defaultValueParser"],
          cellEditor: GridDatePicker,
          cellEditorParams: { dateType: "month", maxDate: MAX_DATE },
          valueFormatter: dateValueFormatter,
          width: 90
        },
      ]
    },
    {
      headerName: "demandPlan", groupId: "demandPlanGrp",
      children: [
        {
          headerName: "periodStart",
          field: "demandPeriodStart",
          cellDataType: 'text',
          type: ["defaultValueParser"],
          cellEditor: GridDatePicker,
          cellEditorParams: { dateType: "month", minDate: MIN_DATE, isFirstDay: true },
          valueFormatter: dateValueFormatter,
          editable: demandPlanEditableHandler,
          cellStyle: demandPlanCellStyle,
          width: 90
        },
        {
          headerName: "numOfMonths",
          field: "demandPeriodVal",
          cellEditor: NumericEditor,
          cellEditorParams: {
            setSnackInfo,
            min: 0,
            max: 120,
          },
          type: ["numberColumn", "rightAligned"],
          editable: demandPlanEditableHandler,
          cellStyle: demandPlanCellStyle,
          valueFormatter: (params) => {
            const value = params?.value;

            if (isNil(value) || value === 0) {
              return "";
            }

            return value;
          },
          width: 110
        }
      ]
    },
    {
      headerName: "domain", field: "classifiDimCd",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter,
      cellEditorParams: {
        values: CLASSIFI_DIM_LIST
      },
      width: 150
    },
    {
      headerName: "base", field: "base",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter,
      cellEditorParams: {
        values: getBaseList
      },
      width: 100
    },
    {
      headerName: "xyzBase", field: "xyzCalcType",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter,
      cellEditorParams: {
        values: getXYZCalcTypeList
      },
      width: 130
    },
    {
      headerName: "abcXyz", groupId: "abcXyzGrp",
      children: [
        {
          headerName: "A",
          field: "aClass",
          cellRenderer: percentValueCellRenderer,
          cellEditor: NumericEditor,
          cellEditorParams: {
            setSnackInfo,
            max: 100,
            sumFields: ["aClass", "bClass"],
            sumNotifyFields: ["aClass", "bClass", "cClass"],
          },
          type: ["numberColumn", "rightAligned"],
          width: 70
        },
        {
          headerName: "B",
          field: "bClass",
          cellRenderer: percentValueCellRenderer,
          cellEditor: NumericEditor,
          cellEditorParams: {
            setSnackInfo,
            max: 100,
            sumFields: ["aClass", "bClass"],
            sumNotifyFields: ["aClass", "bClass", "cClass"],
          },
          type: ["numberColumn", "rightAligned"],
          width: 70
        },
        {
          headerName: "C",
          field: "cClass",
          cellRenderer: percentValueCellRenderer,
          type: ["numberColumn", "rightAligned"],
          editable: false,
          width: 70
        },
        {
          headerName: "X",
          field: "xClass",
          cellRenderer: percentValueCellRenderer,
          cellEditor: NumericEditor,
          cellEditorParams: {
            setSnackInfo,
            sumFields: ["xClass", "yClass"],
          },
          type: ["numberColumn", "rightAligned"],
          width: 70
        },
        {
          headerName: "Y",
          field: "yClass",
          cellRenderer: percentValueCellRenderer,
          cellEditor: NumericEditor,
          cellEditorParams: {
            setSnackInfo,
            sumFields: ["xClass", "yClass"],
          },
          type: ["numberColumn", "rightAligned"],
          width: 70
        },
        {
          headerName: "Z",
          field: "zClass",
          cellRenderer: percentValueCellRenderer,
          editable: false,
          type: ["numberColumn", "rightAligned"],
          width: 70
        },
      ]
    },
    {
      headerName: "exception", groupId: "exceptionGrp",
      children: [
        {
          headerName: "newProduct",
          field: "newProduct",
          cellEditor: NumericEditor,
          cellEditorParams: {
            setSnackInfo,
            maxField: "actualPeriodVal",
          },
          type: ["numberColumn", "rightAligned"],
          width: 100
        },
        {
          headerName: "noSales",
          field: "noSales",
          cellEditor: NumericEditor,
          cellEditorParams: {
            setSnackInfo,
            maxField: "actualPeriodVal",
          },
          type: ["numberColumn", "rightAligned"],
          width: 100
        },
        {
          headerName: "eosPlanned",
          field: "eosPlanned",
          cellEditor: NumericEditor,
          cellEditorParams: {
            setSnackInfo
          },
          type: ["numberColumn", "rightAligned"],
          width: 120
        },
      ]
    },
    { headerName: "createdBy", field: "createdUser", type: "nonEditableColumn", width: 150 },
    { headerName: "createdTs", field: "createdTs", type: "nonEditableColumn", valueFormatter: DateUtils.formatDateTime },
    { headerName: "updatedTs", field: "updatedTs", type: "nonEditableColumn", valueFormatter: DateUtils.formatDateTime }
  ];

  const updatedValidation = (rowData, originData) => {
    const updateRowData = cloneDeep(rowData);
    originData.rowIndex = rowData.rowIndex;
    updateRowData.name = originData.name;

    if (isEqual(updateRowData, originData)) {
      rowData.keepResults = true;
    }
  }

  const saveDataValidation = (saveClassificationData) => {
    const uniqueKeyArray = ["name"];
    const uncheckKeyArray = ["id", "keepResults", "createdUser", "createdTs", "updatedTs", "verNum", "newProduct", "noSales", "eosPlanned", "targetItemType", "actualType", "plantLocAnalysis"];
    uncheckKeyArray.push("actualPeriodEnd", "actualPeriodVal");
    uncheckKeyArray.push("demandPeriodStart", "demandPeriodVal");

    const errColumns = [];
    for (const key in saveClassificationData) {
      if (isNull(saveClassificationData[key]) && !uncheckKeyArray.includes(key)) {
        scenarioGridColumnDefs.forEach((column) => {
          if (column?.field === key) {
            const keyHeader = column?.headerName ? column.headerName : column.field;
            errColumns.push(t(keyHeader));
          }
        });
      }
    }

    if (!isEmpty(errColumns)) {
      const errColumnsMsg = errColumns.join(", ");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${errColumnsMsg}] ${t("MSG_InputMandatory")}` });
      return false;
    }

    const duplicateCheck = {};
    const { aClass, bClass, cClass, actualPeriodEnd, demandPeriodStart, actualPeriodVal, demandPeriodVal } = saveClassificationData;

    if ((isNil(actualPeriodVal) || isEqual(actualPeriodVal, 0)) && (isNil(demandPeriodVal) || isEqual(demandPeriodVal, 0))) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `${t("numOfMonths")} ${t("MSG_ValueGreater")} ${0}` });
      return false;
    }

    if (isEqual(aClass, 0) && isEqual(bClass, 0) && isEqual(cClass, 0)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: `${t("aClass")}, ${t("bClass")}, ${t("cClass")}` }) });

      return false;
    }

    // actualPeriodEnd + actualPeriodVal 조합 또는 demandPeriodStart + demandPeriodVal 조합 중 하나는 반드시 완전히 구성되어야 함
    const hasActualPeriodSet = !isNil(actualPeriodEnd) && !isNil(actualPeriodVal) && !isEqual(actualPeriodVal, 0);
    const hasDemandPeriodSet = !isNil(demandPeriodStart) && !isNil(demandPeriodVal) && !isEqual(demandPeriodVal, 0);

    if (!hasActualPeriodSet && !hasDemandPeriodSet) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: `[${t("numOfMonths")}, ${t("periodEnd")}] OR [${t("periodStart")}, ${t("numOfMonths")}]` }) });
      return false;
    }

    /* 추가 등급분류 Object Data (저장시 이름 중복 체크를 위함) */
    const createdDatas = DataState.getAllStateData(classificationGridRef.current.api).filter((createData) => {
      return !createData?.id
    });

    const allDataList = [...classifications, ...createdDatas];
    for (let i = 0, arraySize = allDataList.length; i < arraySize; i++) {
      const otherClassificationData = allDataList[i];

      for (const uniqueKey of uniqueKeyArray) {
        if (saveClassificationData[uniqueKey] === otherClassificationData[uniqueKey]) {
          duplicateCheck[uniqueKey] = duplicateCheck[uniqueKey] ? duplicateCheck[uniqueKey] + 1 : 1;

          if (duplicateCheck[uniqueKey] > 1) {
            setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${saveClassificationData[uniqueKey]}] ${t("MSG_ScenarioExist")}` });

            return false;
          }
        }
      }
    }


    return true;
  }

  const handleSave = ({ saveDatas }) => {
    classificationService.upsert(saveDatas)
      .then((response) => {
        const upsertResults = response.data;
        const reduxState = reduxUtil.createObjectState(viewName, { classification: upsertResults[0] });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        const { status } = e.response;
        if (status === 409) {
          const targetClassifications = saveDatas.map(data => data.name).join(", ");
          setSnackInfo({ open: true, severity: snackBarMessageColor.optimisticLock, content: t("MSG_OptimisticLockAboutTarget", { target: `[${targetClassifications}]` }) });
        } else {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        }

        debug(e);
      })
      .finally(() => {
        retrieveClassifications();
      })
  }

  const removeRow = ({ selectRow, selectedNodes }) => {
    if (selectRow.id === null) {
      DataState.deleteState(classificationGridRef.current.api, selectedNodes);
    } else {
      classificationService.remove(selectRow.id)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch((e) => {
          const { data } = e.response;
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(data) });
          debug(e);
        })
        .finally(() => {
          retrieveClassifications();
          setSelectedClassification({});
          setClassificationResultSummary([]);
          setChartData([]);
        })
    }
  }

  const handleDialogOpen = (action) => {
    classificationGridRef.current.api.stopEditing(false);

    if (action === DIALOG_TITLE.SAVE) {
      const allStateData = DataState.getAllStateData(classificationGridRef.current.api);
      const updateOrigin = DataState.getUpdateOrigin(classificationGridRef.current.api);

      if (allStateData.length > 0) {
        const saveDatas = [];

        for (let i = 0, length = allStateData.length; i < length; i++) {
          const saveData = cloneDeep(allStateData[i]);
    
          if (!saveDataValidation(saveData)) {
            return;
          }

          if (saveData.id) {
            updatedValidation(saveData, updateOrigin[saveData.rowIndex]);
          }
    
          saveData.classifiObj.base = saveData.base;
          saveData.classifiObj.xyzCalcType = saveData.xyzCalcType;
          saveData.classifiObj.aClass = saveData.aClass;
          saveData.classifiObj.bClass = saveData.bClass;
          saveData.classifiObj.cClass = saveData.cClass;
          saveData.classifiObj.xClass = saveData.xClass;
          saveData.classifiObj.yClass = saveData.yClass;
          saveData.classifiObj.zClass = saveData.zClass;
          saveData.classifiObj.newProduct = saveData.newProduct;
          saveData.classifiObj.noSales = saveData.noSales;
          saveData.classifiObj.eosPlanned = saveData.eosPlanned;
          saveData.classifiObj.plantLocAnalysis = saveData.plantLocAnalysis;
          saveData.classifiObj.actualType = saveData.actualType;
          saveData.classifiObj.targetItemType = saveData.targetItemType;
    
          saveDatas.push(saveData);
        }

        const planversionDataList = saveDatas.filter((data) => !isEmpty(data?.usedVersionIdList) && data?.keepResults !== true);
        
        let translationMessage = t("MSG_SaveConfirm");
        if (!isEmpty(planversionDataList) && planversionDataList.length > 0) {
          translationMessage = t("MSG_ABC_XYZUsedInIpVersion");
        }

        setDialogInfo({ open: true, title: "save", content: translationMessage, el: { saveDatas } });
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      }
    } else if (action === DIALOG_TITLE.DELETE) {
      const selectedNodes = classificationGridRef.current.api.getSelectedNodes();
      const selectRow = selectedNodes[0]?.data;

      if (selectRow) {
        setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm"), el: { selectedNodes, selectRow } });
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
      }
    } else if (action === DIALOG_TITLE.RUN) {
      const selectedNodes = classificationGridRef.current.api.getSelectedNodes();
      const selectRow = selectedNodes[0]?.data;

      if (selectRow) {
        setDialogInfo({ open: true, title: "titleAbcXyzAnalysis", actionName: "RUN_ANALYSIS", content: t("MSG_RunAnalyConfirm") });
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSelectedData") });
      }
    }
  }

  const handleDialog = (action, _falseAction, el) => {
    switch (action) {
      case DIALOG_TITLE.SAVE: {
        handleSave(el);
        break;
      }
      case DIALOG_TITLE.DELETE: {
        removeRow(el);
        break;
      }
      case DIALOG_TITLE.RUN: {
        runAnalysis();
        break;
      }
      default:
        break;
    }

    // redux Classification Filter reload
    reloadClassificationFilter(reduxDispatch);
  }

  useEffect(() => {
    if (gridLoading) {
      retrieveClassifications();
    }

    return () => {
      setClassifications([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading]);

  useEffect(() => {
    if (gridLoading && classifications.length > 0) {
      if (reduxViewState?.classification.id) {
        classificationGridRef.current.api.forEachNode(node => {
          if (isEqual(node.data.id, reduxViewState.classification.id)) {
            node.setSelected(true);
          }
        });
      } else {
        classificationGridRef.current.api.getDisplayedRowAtIndex(0)?.setSelected(true);
      }

      GridUtils.focusOnSelectedRow(classificationGridRef.current.api);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classifications]);

  useEffect(() => {
    const abcXyzGridApi = abcXyzGridRef.current?.api;

    if (!isNil(abcXyzGridApi)) {
      if (classificationResultSummary.length > 0) {
        abcXyzGridApi.selectAll();
      } else {
        abcXyzGridApi.deselectAll();
      }
    }

  }, [classificationResultSummary])

  const onGridReady = (params, gridName) => {
    if (gridName === ABC_XYZ_ANALYSIS_RESULT) {
      GridUtils.setColumnDefs({ ...params, columnDefs: categoryColumnDefs, viewName, gridId: ABC_XYZ_GRID, initState: true });
    } else if (gridName === CLASSIFICATION_RESULT) {
      setGridLoading(true);
      GridUtils.setColumnDefs({ ...params, columnDefs: scenarioGridColumnDefs, viewName, gridId: CLASSIFICATION_GRID, initState: true });
    }
  };

  const addMenuHandleClick = (event) => {
    setAnchorEl(event.currentTarget);
  }

  const ClassificationListMenu = useCallback(() => {
    const addListHandleClose = () => {
      setAnchorEl(null);
    }

    const addListHandleConfirm = (rowData) => {
      const addData = cloneDeep(rowData);
      addListHandleClose();

      DataState.setDataState(GridUtils.addRow(classificationGridRef.current.api, addData));
    }

    return (
      <Menu
        id="demo-positioned-menu"
        aria-labelledby="demo-positioned-button"
        anchorEl={anchorEl}
        open={addListOpen}
        onClose={addListHandleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {defaultScenarioList.map((scenarioItem) => {
          return <MenuItem key={scenarioItem.label} onClick={() => addListHandleConfirm(scenarioItem.value)}>{scenarioItem.label}</MenuItem>
        })}
      </Menu>
    )
  }, [anchorEl, addListOpen, defaultScenarioList]);

  const selectedVersionIsRunning = !isEmpty(selectedClassification.current) && !isNull(selectedClassification.current) && isEqual(selectedClassification.current?.statusCd, RUNNING);
  return (
    <>
      <FilterContainer>
        <TextFieldSearch sx={{ midWidth: 240 }} label={"classificationName"} searchCode={searchCode}
          retrieve={retrieveClassifications} />
        <Box sx={{ ml: "auto" }}>
          <ResultButton disabled={isEmpty(selectedClassification.current) || isNull(selectedClassification.current) || !checkPlanned(selectedClassification.current.statusCd)}
            onClick={() => showResultsPage(selectedClassification.current)} />
          <AddButton
            id="demo-positioned-button"
            aria-label="add"
            aria-controls={addListOpen ? 'demo-positioned-menu' : null}
            aria-haspopup="true"
            aria-expanded={addListOpen ? 'true' : 'false'}
            color="primary"
            size="small"
            onClick={addMenuHandleClick} />
          <ClassificationListMenu />
          <RemoveButton onClick={() => handleDialogOpen(DIALOG_TITLE.DELETE)} disabled={isRunning || selectedVersionIsRunning} />
          <SaveButton onClick={() => handleDialogOpen(DIALOG_TITLE.SAVE)} disabled={isRunning || selectedVersionIsRunning} />
          {(isRunning || selectedVersionIsRunning) ? <RunningButton /> : <RunButton label="abcXyzAnalysis" disabled={isNull(selectedClassification.current?.id)} onClick={() => handleDialogOpen(DIALOG_TITLE.RUN)} />}
        </Box>
      </FilterContainer>
      <Box id="abc-xyz-analysis-grid" className="ag-theme-balham" sx={{ width: "100%", height: "35%" }}>
        <AgGridReact
          ref={classificationGridRef}
          rowData={classifications}
          {...defaultMainGridMemo}
          rowHeight={34}
          rowSelection={{mode: "singleRow", checkboxes: true, enableClickSelection: true}}
          onGridReady={(e) => onGridReady(e, CLASSIFICATION_RESULT)}
          onRowSelected={params => onRowSelected(params, "SCENARIO")}
          onCellValueChanged={onCellValueChanged}
        />
      </Box>
      <Box id={"abc-xyz-analysis"} className={"ip"} sx={{ display: "flex", height: "65%", width: "100%", my: 1 }}>
        {!isEmpty(chartData) ?
          <Box className="base-wrapper">
            <AbcXyzContentArea data={chartData} boundaryNumbers={boundaryNumbers} />
          </Box>
          :
          <Box className="base-wrapper content-center">
            {(isRunning || selectedVersionIsRunning) ?
              <CircularProgress color="inherit" size={"40px"} />
              :
              <Box sx={{ fontSize: FS.TITLE }} component={"span"} color={"grey"}>Please Select Row Data</Box>
            }
          </Box>
        }
        <Box id="abc-xyz-analysis-ag-grid" className="ag-theme-balham grid-area" sx={{ width: "70%", height: "100%", minWidth: "auto" }}>
          <AgGridReact
            ref={abcXyzGridRef}
            rowData={classificationResultSummary}
            {...defualtResultGridMemo}
            rowSelection={{mode: "multiRow", headerCheckbox: true, checkboxes: true}}
            onGridReady={params => onGridReady(params, ABC_XYZ_ANALYSIS_RESULT)}
            onRowSelected={(params) => onRowSelected(params, "CATEGORY")}
          />
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        el={dialogInfo.el}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

const SKU_POST_FIX = "Class";
const SALES_POST_FIX = "Sales";
const AbcXyzContentArea = (props) => {
  const { t } = useTranslation();

  const chartDatas = props?.data;
  const boundaryNumbers = props?.boundaryNumbers;
  const [abcXyzData, setAbcXyzData] = useState({});
  const [yAxisName, setYAxisName] = useState("");

  const volumes = ["A", "B", "C"];
  const uncertainties = ["X", "Y", "Z"];
  const userData = JSON.parse(localStorage.getItem("user"));
  
  const combineData = (result, data) => {
    for (const volume of volumes) {
      for (const uncertainty of uncertainties) {
        const abcXyz = `${volume}${uncertainty}`;
        const abcXyzSales = `${abcXyz}${SALES_POST_FIX}`;

        if (result?.[abcXyz]) {
          result[abcXyz] += data[`${volume.toLowerCase()}${uncertainty.toLowerCase()}${SKU_POST_FIX}`];
          result[abcXyzSales] += data[`${volume.toLowerCase()}${uncertainty.toLowerCase()}${SALES_POST_FIX}`];
        } else {
          result[abcXyz] = data[`${volume.toLowerCase()}${uncertainty.toLowerCase()}${SKU_POST_FIX}`];
          result[abcXyzSales] = data[`${volume.toLowerCase()}${uncertainty.toLowerCase()}${SALES_POST_FIX}`];
        }
      }
    }
  }

  useEffect(() => {
    if (chartDatas) {

      if (yAxisName === "") {
        setYAxisName(GridUtils.gridValueL10N(chartDatas[0]?.baseCd));
      }

      const result = {
        AX: 0,
        AY: 0,
        AZ: 0,
        BX: 0,
        BY: 0,
        BZ: 0,
        CX: 0,
        CY: 0,
        CZ: 0,
        AXSales: 0,
        AYSales: 0,
        AZSales: 0,
        BXSales: 0,
        BYSales: 0,
        BZSales: 0,
        CXSales: 0,
        CYSales: 0,
        CZSales: 0,
        TOTAL: 0,
        TOTAL_SALES: 0,
      };

      for (const chartData of chartDatas) {
        combineData(result, chartData);
      }

      result.A = result.AX + result.AY + result.AZ;
      result.B = result.BX + result.BY + result.BZ;
      result.C = result.CX + result.CY + result.CZ;
      result.X = result.AX + result.BX + result.CX;
      result.Y = result.AY + result.BY + result.CY;
      result.Z = result.AZ + result.BZ + result.CZ;

      result.TOTAL = result.A + result.B + result.C;
      result.TOTAL_SALES = result.AXSales + result.AYSales + result.AZSales + result.BXSales + result.BYSales + result.BZSales + result.CXSales + result.CYSales + result.CZSales;
      setAbcXyzData(result);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartDatas]);

  const chartValueFormatter = (value, type) => {
    const base = chartDatas?.[0]?.baseCd ?? QUANTITY;
    if (type !== "sku" && (base === PROFIT || base === REVENUE || base === AMOUNT || base === COGS)) {
      const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
      return userData?.currencySymbol + formatValue;
    }
    return NumberUtil.formatNumber(value, 0);
  }

  const chartPercentageFormatter = (value) => {
    return NumberUtil.formatNumber(value, { style: "percent", maximumFractionDigits: 2 });
  }

  const percentageCalc = (paramValue) => {
    const total = abcXyzData?.TOTAL ? abcXyzData.TOTAL : 0;
    const value = paramValue && total !== 0 ? paramValue / total : 0;

    return chartPercentageFormatter(value);
  }

  const AbcXyzYAxis = useCallback(() => {
    const valueA = abcXyzData?.A ? abcXyzData.A : 0;
    const valueB = abcXyzData?.B ? abcXyzData.B : 0;
    const valueC = abcXyzData?.C ? abcXyzData.C : 0;

    return (
      <Box sx={{ width: "100%", height: "100%", display: "inline-flex" }}>
        <Box sx={{ width: "30%", minWidth: "30px", height: "100%" }}>
          <Box className="default-wh display-if content-center" sx={{ wordBreak: "keep-all", transform: "rotate(-90deg)", fontWeight: "bold" }}>{yAxisName}</Box>
        </Box>
        <Box sx={{ width: "70%", height: "100%", display: "inline-block" }}>
          <Box className="vertical-wrapper default-wh content-center">
            <Box sx={{ display: "inline-block" }}>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.KPI, fontWeight: "500" }}>A</Box>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{chartValueFormatter(valueA, "sku")}</Box>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{`(${percentageCalc(valueA)})`}</Box>
            </Box>
          </Box>
          <Box className="vertical-wrapper default-wh content-center">
            <Box sx={{ display: "inline-block" }}>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.KPI, fontWeight: "500" }}>B</Box>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{chartValueFormatter(valueB, "sku")}</Box>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{`(${percentageCalc(valueB)})`}</Box>
            </Box>
          </Box>
          <Box className="vertical-wrapper default-wh content-center">
            <Box sx={{ display: "inline-block" }}>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.KPI, fontWeight: "500" }}>C</Box>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{chartValueFormatter(valueC, "sku")}</Box>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{`(${percentageCalc(valueC)})`}</Box>
            </Box>
          </Box>
        </Box>
      </Box>
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abcXyzData, yAxisName])

  const AbcXyzXAxis = useCallback(() => {
    const valueX = abcXyzData?.X ? abcXyzData.X : 0;
    const valueY = abcXyzData?.Y ? abcXyzData.Y : 0;
    const valueZ = abcXyzData?.Z ? abcXyzData.Z : 0;

    return (
      <Box className="default-wh" sx={{ display: "inline-block" }}>
        <Box className="display-if" sx={{ alignItems: "center", mt: 0.5, width: "100%", height: "80%" }}>
          <Box sx={{ width: "16%", minWidth: "120px", height: "100%" }} />
          <Box className="default-wh">
            <Box className="horizon-wrapper default-wh" sx={{ display: "inline-block" }}>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.KPI, fontWeight: "500" }}>X</Box>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{`${chartValueFormatter(valueX, "sku")} (${percentageCalc(valueX)})`}</Box>
            </Box>
            <Box className="horizon-wrapper default-wh" sx={{ display: "inline-block" }}>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.KPI, fontWeight: "500" }}>Y</Box>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{`${chartValueFormatter(valueY, "sku")} (${percentageCalc(valueY)})`}</Box>
            </Box>
            <Box className="horizon-wrapper default-wh" sx={{ display: "inline-block" }}>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.KPI, fontWeight: "500" }}>Z</Box>
              <Box className="content-center" sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{`${chartValueFormatter(valueZ, "sku")} (${percentageCalc(valueZ)})`}</Box>
            </Box>
          </Box>
          <Box sx={{ width: "16%", minWidth: "120px", height: "100%" }} />
        </Box>
        <Box className="display-if content-center" sx={{ width: "100%", height: "auto", mt: 2 }}>
          <Box sx={{ textAlign: "center", fontWeight: "bold" }}>{t(UNCERTAINTIES)}</Box>
        </Box>
      </Box>
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abcXyzData])

  const MakeAbcXyzChartBox = ({ abcXyzChartData }) => {
    const MakeBox = ({ volume, uncertainty }) => {
      const chartClass = `${volume}${uncertainty}`;
      const alignClassName = "horizon-wrapper";

      const skusValue = abcXyzChartData?.[chartClass];
      const salesValue = abcXyzChartData?.[`${chartClass}${SALES_POST_FIX}`];
      const classInfo = CodeUtil.ABCClassConfig[chartClass];

      const className = classInfo?.className ? classInfo.className : "";

      const chartSkusValue = skusValue || skusValue === 0 ? `${chartValueFormatter(skusValue, "sku")} ${t(ITEM)}` : "";
      const chartSalesValue = salesValue || salesValue === 0 ? `${chartValueFormatter(salesValue, "value")}` : "";

      let chartSkusValuePercentage = 0;
      let chartSalesValuePercentage = 0;

      if (skusValue || skusValue === 0) {
        chartSkusValuePercentage = abcXyzChartData.TOTAL !== 0 ? Number(skusValue / abcXyzChartData.TOTAL) : 0;
      }

      if (salesValue || salesValue === 0) {
        chartSalesValuePercentage = abcXyzChartData.TOTAL_SALES !== 0 ? Number(salesValue / abcXyzChartData.TOTAL_SALES) : 0;
      }

      chartSkusValuePercentage = chartPercentageFormatter(chartSkusValuePercentage);
      chartSalesValuePercentage = chartPercentageFormatter(chartSalesValuePercentage);

      const keyValue = `${chartClass}${skusValue || skusValue === 0 ? skusValue : "_none"}`;
      return (
        <Box key={keyValue} className={`${alignClassName} abc-xyz-chart ${className} content-center`} sx={{ display: "inline-flex !important" }}>
          <Box>
            <Box sx={{ width: "100%", height: "auto", fontSize: FS.TITLE, marginBottom: 1 }}>{`${chartSkusValue} (${chartSkusValuePercentage})`}</Box>
            <Box sx={{ width: "100%", height: "auto", fontSize: FS.TITLE }}>{`${chartSalesValue} (${chartSalesValuePercentage})`}</Box>
          </Box>
        </Box>
      );
    }

    const MakeBoxCombined = ({ volume }) => {
      const className = "vertical-wrapper";
      return (
        <Box className={className} >
          {uncertainties.map((uncertainty) => {
            return <MakeBox key={`${volume}${uncertainty}`} volume={volume} uncertainty={uncertainty} />
          })}
        </Box>
      )
    }

    return (
      volumes.map((volume) => {
        return <MakeBoxCombined key={volume} volume={volume} />
      })
    )
  }

  const BoundaryPosition = () => {
    return (
      <Box>
        <Box sx={{ display: "inline-flex", position: "absolute", left: "31%", top: "-25px" }}>
          {`${boundaryNumbers?.x}%`}
        </Box>

        <Box sx={{ display: "inline-flex", position: "absolute", left: "63%", top: "-25px" }}>
          {`${boundaryNumbers?.y}%`}
        </Box>

        <Box sx={{ display: "inline-flex", position: "absolute", right: "-40px", top: "30%" }}>
          {`${boundaryNumbers?.a}%`}
        </Box>

        <Box sx={{ display: "inline-flex", position: "absolute", right: "-40px", top: "64%" }}>
          {`${boundaryNumbers?.b}%`}
        </Box>
      </Box>
    )
  }

  const BaseBox = useCallback(() => {
    return (
      <Box className="default-wh" sx={{ display: "inline-block" }}>
        <Box className="display-if base-box">

          {/* ABC-XYZ Chart Y axis */}
          <Box sx={{ minWidth: "120px", width: "16%", height: "100%", display: "inline-flex" }}>
            <AbcXyzYAxis />
          </Box>

          {/* ABC-XYZ Chart Body Area */}
          <Box sx={{ width: "68%", height: "100%", display: "inline-block" }}>
            <Box sx={{ position: "relative" }} className="default-wh">
              <BoundaryPosition />
              <MakeAbcXyzChartBox abcXyzChartData={abcXyzData} />
            </Box>
          </Box>

          <Box sx={{ minWidth: "120px", width: "16%", height: "100%", display: "inline-flex" }} />
        </Box>

        <Box sx={{ width: "100%", minHeight: "80px", height: "auto" }}>
          {/* ABC-XYZ Chart X axis */}
          <AbcXyzXAxis />
        </Box>
      </Box>
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abcXyzData])


  return (
    <Box sx={{ width: "100%", height: "98%", overflow: "auto" }}>

      {/* Top Area */}
      <Box className="content-center" sx={{ width: "100%", height: "7%", mb: 1 }}>
      </Box>

      {/* Chart Content Area */}
      <Box sx={{ width: "100%", minHeight: "300px", height: "90%" }}>
        <BaseBox />
      </Box>

      {/* Bottom Area */}
      {/* <Box sx={{width: "100%", height: "auto", textAlign: "center"}}> <h3 style={{margin: 0}}> Uncertainties </h3> </Box> */}
    </Box>
  );
};

export default withTranslation()(AbcXyzAnalysisScenarios);
